import { useContext, useEffect, useRef, useState } from "react";
import { useSessionStorage } from "usehooks-ts";
import { getBannerTextColor } from "../../utils/publicBannerColors";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { ActionButton } from "../Common/Buttons/ActionButton";
import {
  BannerContainer,
  BannerContent,
  BannerWrapper,
  ReadMoreButton,
} from "./__styles__/PublicBanner";

interface SessionState {
  publicBannerDismissed: boolean;
}

export const PublicBanner = () => {
  const { account } = useContext(AuthContext);
  const { bannerContent, bannerColor, bannerDisplayed } =
    account?.publicPortal || {};

  const [sessionState, setSessionState] = useSessionStorage<SessionState>(
    "account",
    { publicBannerDismissed: false },
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleDismiss = () => {
    setSessionState({
      ...sessionState,
      publicBannerDismissed: true,
    });
  };

  const checkOverflow = () => {
    const element = wrapperRef.current?.firstElementChild;
    if (!element) return;

    setHasOverflow(element.scrollHeight > element.clientHeight);
  };

  const trackReadMoreClicked = () => {
    track("Public Website Banner Read More Clicked", {
      "Banner Color": bannerColor,
      "Banner Content": bannerContent,
    });
  };

  useEffect(() => {
    checkOverflow();
  }, [bannerContent]);

  useEffect(() => {
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);

  if (!bannerDisplayed || sessionState.publicBannerDismissed) {
    return null;
  }

  const textColor = getBannerTextColor(bannerColor || "");

  return (
    <BannerContainer
      css={{ backgroundColor: bannerColor || "", color: textColor }}
      textColor={textColor}
      role="banner"
    >
      <BannerWrapper ref={wrapperRef}>
        <BannerContent
          markdown={bannerContent || ""}
          isExpanded={isExpanded}
          options={{ simpleLineBreaks: true }}
        />
        <div>
          <ActionButton
            icon="x"
            variant={textColor === "white" ? "inverted" : "default"}
            onClick={handleDismiss}
            aria-label="Close banner"
          />
        </div>
      </BannerWrapper>
      {!isExpanded && hasOverflow && (
        <ReadMoreButton
          onClick={() => {
            trackReadMoreClicked();
            setIsExpanded(true);
          }}
          textColor={textColor}
          aria-label="Read more banner content"
        >
          Read more
        </ReadMoreButton>
      )}
    </BannerContainer>
  );
};
